import { useState, useEffect } from 'react';
import whiteLabelService from '../../services/whiteLabelService';
import useAutoSave from '../../hooks/useAutoSave';
import AutoSaveStatus from '../Global/AutoSaveStatus';
import './scss/SmtpConfiguration.scss';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

const SmtpConfiguration = () => {
  const [smtp, setSmtp] = useState({
    enabled: false,
    host: '',
    port: 587,
    secure: false,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    verified: false,
    hasPassword: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Validation function for SMTP configuration
  const validateSmtpConfig = (data) => {
    const errors = {};
    const smtpData = data.smtp || data;

    if (smtpData.enabled) {
      if (!smtpData.host) {
        errors.host = 'SMTP host is required';
      }
      if (!smtpData.port) {
        errors.port = 'Port is required';
      }
      if (!smtpData.username) {
        errors.username = 'Username is required';
      }
      if (!smtpData.fromEmail) {
        errors.fromEmail = 'From email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(smtpData.fromEmail)) {
        errors.fromEmail = 'Invalid email format';
      }
    }

    return errors;
  };

  // Update function for auto-save
  const handleUpdate = async (data) => {
    try {
      await whiteLabelService.updateSmtpConfig(data.smtp);
    } catch (error) {
      console.error('Error updating SMTP config:', error);
      throw error;
    }
  };

  const { autoSave, autoSaving, lastSaved, errors, setFieldErrors, clearFieldError } = useAutoSave(
    handleUpdate,
    validateSmtpConfig
  );

  // Load SMTP configuration
  useEffect(() => {
    loadSmtpConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSmtpConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await whiteLabelService.getSmtpConfig();
      setSmtp(response);
      setFieldErrors({});
    } catch (error) {
      console.error('Error loading SMTP config:', error);
      setFieldErrors({ general: error.message || 'Failed to load SMTP configuration' });
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleInputChange = (field, value) => {
    const updatedSmtp = {
      ...smtp,
      [field]: value
    };

    setSmtp(updatedSmtp);
    clearFieldError(field);

    // Auto-save the changes
    autoSave({ smtp: updatedSmtp });
  };

  const handleTestSmtp = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setFieldErrors({});

      const response = await whiteLabelService.testSmtpConfig(testEmail);

      if (response.success) {
        setTestResult({
          success: true,
          message: response.message
        });
        // Reload config to get updated verification status
        await loadSmtpConfig();
      } else {
        setTestResult({
          success: false,
          message: response.error || 'SMTP test failed'
        });
      }
    } catch (error) {
      console.error('Error testing SMTP:', error);
      setTestResult({
        success: false,
        message: error.message || 'Failed to test SMTP configuration'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDisableSmtp = async () => {
    if (!window.confirm('Are you sure you want to disable custom SMTP? Emails will be sent from Zoobbe\'s SMTP server.')) {
      return;
    }

    try {
      await whiteLabelService.disableSmtpConfig();
      await loadSmtpConfig();
      setTestResult(null);
    } catch (error) {
      console.error('Error disabling SMTP:', error);
      setFieldErrors({ general: error.message || 'Failed to disable SMTP' });
    }
  };

  const handleDeleteSmtp = async () => {
    if (!window.confirm('Are you sure you want to delete SMTP configuration? This will remove all SMTP settings.')) {
      return;
    }

    try {
      await whiteLabelService.deleteSmtpConfig();
      await loadSmtpConfig();
      setTestResult(null);
    } catch (error) {
      console.error('Error deleting SMTP:', error);
      setFieldErrors({ general: error.message || 'Failed to delete SMTP configuration' });
    }
  };

  const smtpProviders = [
    {
      name: 'Gmail',
      host: 'smtp.gmail.com',
      port: 587,
      secure: false,
      note: 'Requires App Password if 2FA is enabled'
    },
    {
      name: 'SendGrid',
      host: 'smtp.sendgrid.net',
      port: 587,
      secure: false,
      note: 'Username should be "apikey"'
    },
    {
      name: 'Mailgun',
      host: 'smtp.mailgun.org',
      port: 587,
      secure: false,
      note: 'Use your Mailgun SMTP credentials'
    },
    {
      name: 'AWS SES',
      host: 'email-smtp.us-east-1.amazonaws.com',
      port: 587,
      secure: false,
      note: 'Region-specific host'
    }
  ];

  const applyProviderTemplate = (provider) => {
    const updatedSmtp = {
      ...smtp,
      host: provider.host,
      port: provider.port,
      secure: provider.secure
    };
    setSmtp(updatedSmtp);
    autoSave({ smtp: updatedSmtp });
  };

  if (loadingConfig) {
    return (
      <div className="smtp-configuration">
        <div className="loading-state">
          <span className="material-symbols-outlined spinning">progress_activity</span>
          <p>Loading SMTP configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="smtp-configuration">
      <div className="smtp-header">
        <div>
          <h3>SMTP Configuration</h3>
          <p>Configure custom SMTP server to send emails from your own domain</p>
        </div>
        <div className="smtp-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={smtp.enabled}
              onChange={(e) => handleInputChange('enabled', e.target.checked)}
            />
            <span className="toggle-switch"></span>
            <span className="toggle-text">{smtp.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
      </div>

      {errors.general && (
        <div className="error-banner">
          <span className="material-symbols-outlined">error</span>
          <span>{errors.general}</span>
        </div>
      )}

      {smtp.verified && (
        <div className="success-banner">
          <CircleCheckOutlined size={20} color='var(--success-color)' />
          <span>SMTP configuration verified and active</span>
          {smtp.lastVerifiedAt && (
            <small>Last verified: {new Date(smtp.lastVerifiedAt).toLocaleString()}</small>
          )}
        </div>
      )}

      {!smtp.verified && smtp.enabled && smtp.verificationError && (
        <div className="warning-banner">
          <span className="material-symbols-outlined">warning</span>
          <span>Verification failed: {smtp.verificationError}</span>
        </div>
      )}

      {smtp.enabled && (
        <>
          <div className="smtp-section">
            <h4>Quick Setup Templates</h4>
            <div className="provider-templates">
              {smtpProviders.map((provider) => (
                <button
                  key={provider.name}
                  type="button"
                  onClick={() => applyProviderTemplate(provider)}
                  className="provider-template-btn"
                  disabled={autoSaving}
                >
                  <span className="provider-name">{provider.name}</span>
                  <small>{provider.note}</small>
                </button>
              ))}
            </div>
          </div>

          <div className="smtp-section">
            <h4>Server Settings</h4>
            <div className="form-row">
              <div className="form-group">
                <label>SMTP Host *</label>
                <input
                  type="text"
                  value={smtp.host}
                  onChange={(e) => handleInputChange('host', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className={errors.host ? 'error' : ''}
                />
                {errors.host && <span className="error-text">{errors.host}</span>}
              </div>

              <div className="form-group">
                <label>Port *</label>
                <input
                  type="number"
                  value={smtp.port}
                  onChange={(e) => handleInputChange('port', parseInt(e.target.value))}
                  placeholder="587"
                  className={errors.port ? 'error' : ''}
                />
                {errors.port && <span className="error-text">{errors.port}</span>}
              </div>
            </div>
          </div>

          <div className="smtp-section">
            <h4>Authentication</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={smtp.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="your-email@domain.com"
                  className={errors.username ? 'error' : ''}
                />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>

              <div className="form-group">
                <label>Password *</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={smtp.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={smtp.hasPassword ? '••••••••' : 'Enter SMTP password'}
                    className={errors.password ? 'error' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                  >
                    <span className="material-symbols-outlined">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
                {smtp.hasPassword && !smtp.password && (
                  <small>Password is set. Leave blank to keep current password.</small>
                )}
              </div>
            </div>
          </div>

          <div className="smtp-section">
            <h4>Sender Email</h4>
            <div className="form-group">
              <label>From Email *</label>
              <input
                type="email"
                value={smtp.fromEmail}
                onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                placeholder="noreply@yourdomain.com"
                className={errors.fromEmail ? 'error' : ''}
              />
              {errors.fromEmail && <span className="error-text">{errors.fromEmail}</span>}
            </div>
          </div>

          <div className="smtp-section">
            <h4>Test Configuration</h4>
            <div className="test-smtp">
              <div className="form-row">
                <div className="form-group">
                  <label>Test Email Address</label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Leave blank to send to your account email"
                  />
                </div>
                <div className="form-group test-button-wrapper">
                  <button
                    type="button"
                    onClick={handleTestSmtp}
                    disabled={testing || autoSaving || !smtp.host || !smtp.username}
                    className="btn btn-primary"
                  >
                    {testing ? (
                      <>
                        <span className="material-symbols-outlined spinning">progress_activity</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined">send</span>
                        Send Test Email
                      </>
                    )}
                  </button>
                </div>
              </div>

              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  <span className="material-symbols-outlined">
                    {testResult.success ? 'check_circle' : 'error'}
                  </span>
                  <span>{testResult.message}</span>
                </div>
              )}
            </div>
          </div>

          <div className="smtp-section danger-zone">
            <h4>Danger Zone</h4>
            <div className="danger-actions">
              <button
                type="button"
                onClick={handleDisableSmtp}
                disabled={autoSaving}
                className="btn btn-outline btn-warning"
              >
                <span className="material-symbols-outlined">block</span>
                Disable SMTP
              </button>

              <button
                type="button"
                onClick={handleDeleteSmtp}
                disabled={autoSaving}
                className="btn btn-outline btn-danger"
              >
                <span className="material-symbols-outlined">delete</span>
                Delete Configuration
              </button>
            </div>
          </div>
        </>
      )}

      {!smtp.enabled && (
        <div className="smtp-disabled-info">
          <span className="material-symbols-outlined">info</span>
          <div>
            <h4>Custom SMTP is disabled</h4>
            <p>Emails are currently being sent from Zoobbe's default SMTP server.</p>
            <p>Enable custom SMTP to send emails from your own domain and improve email deliverability.</p>
          </div>
        </div>
      )}

      <AutoSaveStatus
        autoSaving={autoSaving}
        lastSaved={lastSaved}
      />
    </div>
  );
};

export default SmtpConfiguration;
